import React, { useState, useCallback } from "react";
import { Button, Card, Alert, ProgressBar, Badge, Table, Form, Row, Col } from "react-bootstrap";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { read, utils } from "xlsx";

const UploadMembers = () => {
  const [memberFile, setMemberFile] = useState(null);
  const [loanFile, setLoanFile] = useState(null);
  const [loanDueFile, setLoanDueFile] = useState(null);  // NEW: Loan Due Details
  const [transactionFile, setTransactionFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState("3"); // Default UCH branch
  const [fileStats, setFileStats] = useState({
    members: 0,
    loans: 0,
    loanDues: 0,
    transactions: 0,
  });

  // Branch options
  const branches = [
    { id: "1", name: "Rameswaram (RMM)" },
    { id: "3", name: "Uchipuli (UCH)" },
    { id: "4", name: "Pamban" },
  ];

  // Handle file selection
  const handleMemberFileChange = (event) => {
    const file = event.target.files[0];
    setMemberFile(file);
    setError(null);
    setStats(null);
    if (file) {
      parseFileForStats(file, "members");
    }
  };

  const handleLoanFileChange = (event) => {
    const file = event.target.files[0];
    setLoanFile(file);
    setError(null);
    setStats(null);
    if (file) {
      parseFileForStats(file, "loans");
    }
  };

  const handleLoanDueFileChange = (event) => {
    const file = event.target.files[0];
    setLoanDueFile(file);
    setError(null);
    setStats(null);
    if (file) {
      parseFileForStats(file, "loanDues");
    }
  };

  const handleTransactionFileChange = (event) => {
    const file = event.target.files[0];
    setTransactionFile(file);
    setError(null);
    setStats(null);
    if (file) {
      parseFileForStats(file, "transactions");
    }
  };

  // Parse file just for stats preview
  const parseFileForStats = (file, type) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const arrayBuffer = event.target.result;
        const workbook = read(arrayBuffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const data = utils.sheet_to_json(worksheet);
        setFileStats((prev) => ({ ...prev, [type]: data.length }));
      } catch (err) {
        console.error("Error parsing file for stats:", err);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Trim empty values from data
  const trimData = (data) => {
    return data.map((row) =>
      Object.fromEntries(
        Object.entries(row).filter(
          ([_, value]) => value !== "" && value !== undefined && value !== null
        )
      )
    );
  };

  // Find the actual header row (skip title rows like "Savings Report")
  const findHeaderRow = (worksheet) => {
    const range = utils.decode_range(worksheet['!ref']);

    // Look for a row that contains known column headers
    const knownHeaders = ['member id', 'unique memberid', 'accno', 'ledgerid', 'name', 's.no', 'transaction date', 'member name'];

    // Search first 10 rows for header
    for (let row = range.s.r; row <= Math.min(range.s.r + 10, range.e.r); row++) {
      let foundHeaders = 0;
      for (let col = range.s.c; col <= Math.min(range.s.c + 15, range.e.c); col++) {
        const cellAddress = utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];
        if (cell && cell.v) {
          const cellValue = String(cell.v).trim().toLowerCase();
          if (knownHeaders.some(h => cellValue.includes(h) || h.includes(cellValue))) {
            foundHeaders++;
            if (foundHeaders >= 2) {
              console.log(`Found header row at index ${row} with "${cellValue}"`);
              return row;
            }
          }
        }
      }
    }
    return 0; // Default to first row if no known headers found
  };

  // Parse CSV file as text in chunks (for large files)
  const parseCSVStreaming = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target.result;
          const lines = text.split(/\r?\n/);

          if (lines.length === 0) {
            resolve([]);
            return;
          }

          // Find header row (first non-empty line with data)
          let headerIndex = 0;
          let headers = [];
          for (let i = 0; i < Math.min(10, lines.length); i++) {
            const line = lines[i].trim();
            if (line && line.includes(',')) {
              headers = parseCSVLine(line);
              // Check if this looks like a header row (has known fields)
              const knownHeaders = ['AccNo', 'LedgerID', 'MemberCode', 'LoanID', 'CustomerID', 'Name', 'PhoneNumber'];
              if (headers.some(h => knownHeaders.some(kh => h.toLowerCase().includes(kh.toLowerCase())))) {
                headerIndex = i;
                break;
              }
            }
          }

          // If no header found, use first line
          if (headers.length === 0) {
            headers = parseCSVLine(lines[0]);
          }

          console.log(`[CSV Parser] Headers found at line ${headerIndex}:`, headers.slice(0, 10));

          // Parse data rows
          const data = [];
          const startRow = headerIndex + 1;
          const maxRows = 500000; // Safety limit

          for (let i = startRow; i < lines.length && data.length < maxRows; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            const values = parseCSVLine(line);
            if (values.length < 2) continue; // Skip empty rows

            const row = {};
            for (let j = 0; j < headers.length; j++) {
              const header = headers[j].trim();
              if (header) {
                row[header] = values[j] !== undefined ? String(values[j]).trim() : '';
              }
            }

            // Only add rows with meaningful data
            const meaningfulValues = Object.values(row).filter(v => v !== '' && v !== undefined);
            if (meaningfulValues.length >= 2) {
              data.push(row);
            }
          }

          console.log(`[CSV Parser] Parsed ${data.length} rows from ${file.name}`);
          if (data.length > 0) {
            console.log('Sample row:', data[0]);
          }

          resolve(data);
        } catch (err) {
          console.error('[CSV Parser] Error:', err);
          reject(err);
        }
      };
      reader.onerror = (err) => reject(err);
      reader.readAsText(file); // Read as text, not binary
    });
  };

  // Parse a single CSV line (handles quoted values with commas)
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim()); // Last value
    return result;
  };

  // Parse file - uses streaming for large CSV, xlsx for smaller files
  const parseFile = (file) => {
    return new Promise(async (resolve, reject) => {
      try {
        const fileSize = file.size;
        const isCSV = file.name.toLowerCase().endsWith('.csv');
        const LARGE_FILE_THRESHOLD = 1 * 1024 * 1024; // 1MB - Lower threshold to force streaming parser for safety with long IDs

        // For large CSV files, use streaming parser
        if (isCSV && fileSize > LARGE_FILE_THRESHOLD) {
          console.log(`[Large File] Using streaming parser for ${file.name} (${(fileSize / 1024 / 1024).toFixed(1)}MB)`);
          const data = await parseCSVStreaming(file);
          resolve(data);
          return;
        }

        // For smaller files or non-CSV, use xlsx library
        const fileReader = new FileReader();
        fileReader.onload = (event) => {
          try {
            const arrayBuffer = event.target.result;
            const workbook = read(arrayBuffer, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            // Find the actual header row
            const headerRow = findHeaderRow(worksheet);

            // Parse with correct header row
            let data;
            if (headerRow > 0) {
              // Skip rows before the actual header
              const range = utils.decode_range(worksheet['!ref']);
              range.s.r = headerRow;
              worksheet['!ref'] = utils.encode_range(range);
              data = utils.sheet_to_json(worksheet, { raw: false, defval: "" });
            } else {
              data = utils.sheet_to_json(worksheet, { raw: false, defval: "" });
            }

            data = trimData(data);

            // Trim column names (handles " Unique MemberID" -> "Unique MemberID")
            data = data.map(row => {
              const trimmedRow = {};
              for (const [key, value] of Object.entries(row)) {
                trimmedRow[key.trim()] = value;
              }
              return trimmedRow;
            });

            // Filter out empty rows or rows with no meaningful data
            data = data.filter(row => {
              const values = Object.values(row);
              // Keep row if it has at least 2 non-empty values
              return values.filter(v => v !== '' && v !== undefined && v !== null).length >= 2;
            });

            console.log(`Parsed ${file.name}: ${data.length} rows, headerRow: ${headerRow}`);
            if (data.length > 0) {
              console.log('Sample columns:', Object.keys(data[0]));
              console.log('Sample row:', data[0]);
            }

            resolve(data);
          } catch (err) {
            // If xlsx fails, try CSV streaming parser as fallback
            if (isCSV) {
              console.log(`[Fallback] xlsx failed, trying streaming parser for ${file.name}`);
              parseCSVStreaming(file).then(resolve).catch(reject);
            } else {
              reject(err);
            }
          }
        };
        fileReader.onerror = (err) => reject(err);
        fileReader.readAsArrayBuffer(file);
      } catch (err) {
        reject(err);
      }
    });
  };

  // Upload chunks to backend
  const uploadChunks = async (data, endpoint, chunkSize = 50) => {
    const totalChunks = Math.ceil(data.length / chunkSize);
    for (let i = 0; i < totalChunks; i++) {
      const chunk = data.slice(i * chunkSize, (i + 1) * chunkSize);
      try {
        await axios.post(`${API_BASE_URL}/api/${endpoint}`, { chunk });
      } catch (err) {
        throw new Error(`Error uploading chunk ${i + 1}: ${err.message}`);
      }
    }
    return totalChunks;
  };

  // LARGE FILE: Upload and process transactions in batches (for files > 50K records)
  // This prevents memory exhaustion by processing each batch immediately
  const uploadAndProcessTransactionBatches = async (data, branchId, onProgress) => {
    const BATCH_SIZE = 2000;  // Process 2000 transactions at a time
    const totalBatches = Math.ceil(data.length / BATCH_SIZE);
    let totalProcessed = 0;
    let totalSkipped = 0;
    let totalErrors = 0;

    console.log(`[LARGE FILE] Processing ${data.length} transactions in ${totalBatches} batches...`);

    for (let i = 0; i < totalBatches; i++) {
      const batch = data.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE);

      try {
        const response = await axios.post(`${API_BASE_URL}/api/processTransactionBatch`, {
          chunk: batch,
          branchId: branchId
        });

        if (response.data.success) {
          totalProcessed += response.data.stats.processed;
          totalSkipped += response.data.stats.skipped;
          totalErrors += response.data.stats.errors;
        }

        // Update progress
        const batchProgress = Math.round(((i + 1) / totalBatches) * 100);
        if (onProgress) {
          onProgress(batchProgress, totalProcessed, data.length);
        }

      } catch (err) {
        console.error(`Batch ${i + 1} failed:`, err.message);
        totalErrors += batch.length;
      }
    }

    return {
      processed: totalProcessed,
      skipped: totalSkipped,
      errors: totalErrors,
      total: data.length
    };
  };

  // Handle file upload
  const handleFileUpload = useCallback(async () => {
    if (!memberFile && !loanFile && !loanDueFile && !transactionFile) {
      setError("Please select at least one file to upload.");
      return;
    }

    setLoading(true);
    setProgress(0);
    setStatus("Initializing...");
    setError(null);
    setStats(null);

    try {
      // Clear any previous chunks
      setStatus("Clearing previous data...");
      await axios.post(`${API_BASE_URL}/api/clearChunks`);
      setProgress(5);

      let totalFiles = 0;
      let processedFiles = 0;

      if (memberFile) totalFiles++;
      if (loanFile) totalFiles++;
      if (loanDueFile) totalFiles++;
      if (transactionFile) totalFiles++;

      const progressPerFile = 85 / totalFiles;

      // Parse and upload member data
      if (memberFile) {
        setStatus("Parsing member file...");
        const memberData = await parseFile(memberFile);
        setProgress(10);
        setStatus(`Uploading ${memberData.length} member records...`);
        await uploadChunks(memberData, "uploadMemberChunk", 100);
        processedFiles++;
        setProgress(10 + progressPerFile * processedFiles);
      }

      // Parse and upload loan data
      if (loanFile) {
        setStatus("Parsing loan file...");
        const loanData = await parseFile(loanFile);
        setStatus(`Uploading ${loanData.length} loan records...`);
        await uploadChunks(loanData, "uploadLoanChunk", 100);
        processedFiles++;
        setProgress(10 + progressPerFile * processedFiles);
      }

      // Parse and upload loan due details (UCH specific)
      if (loanDueFile) {
        setStatus("Parsing loan due details file...");
        const loanDueData = await parseFile(loanDueFile);
        setStatus(`Uploading ${loanDueData.length} loan due records...`);
        await uploadChunks(loanDueData, "uploadLoanDueChunk", 100);
        processedFiles++;
        setProgress(10 + progressPerFile * processedFiles);
      }

      // VITAL: Process Member and Loan data into database BEFORE transaction batching
      // This ensures customers exist in DB for transactions to link to
      if (memberFile || loanFile || loanDueFile) {
        setStatus("Processing member and loan data first...");
        const processResponse = await axios.post(`${API_BASE_URL}/api/processAllCsvData`, {
          branchId: parseInt(selectedBranch)
        });

        if (!processResponse.data.success) {
          throw new Error("Failed to process member data: " + processResponse.data.message);
        }
        console.log("Member/Loan data processed successfully");
      }

      // Parse and upload transaction data
      // For LARGE files (> 50,000 records), use batch processing
      let transactionStats = null;
      if (transactionFile) {
        setStatus("Parsing transaction file...");
        const transactionData = await parseFile(transactionFile);


        const LARGE_FILE_THRESHOLD = 50000;  // 50K records

        if (transactionData.length > LARGE_FILE_THRESHOLD) {
          // LARGE FILE MODE: Process in batches immediately
          setStatus(`📊 Large file detected (${transactionData.length.toLocaleString()} records). Using batch processing...`);

          transactionStats = await uploadAndProcessTransactionBatches(
            transactionData,
            parseInt(selectedBranch),
            (batchProgress, processed, total) => {
              setProgress(10 + progressPerFile * processedFiles + (progressPerFile * batchProgress / 100));
              setStatus(`⏳ Processing transactions: ${processed.toLocaleString()} / ${total.toLocaleString()} (${batchProgress}%)`);
            }
          );

          processedFiles++;
          setProgress(10 + progressPerFile * processedFiles);
          setStatus(`✅ Transactions: ${transactionStats.processed.toLocaleString()} processed, ${transactionStats.skipped.toLocaleString()} skipped`);

        } else {
          // NORMAL MODE: Upload chunks then process together
          setStatus(`Uploading ${transactionData.length} transaction records...`);
          await uploadChunks(transactionData, "uploadTransactionChunk", 500);
          processedFiles++;
          setProgress(10 + progressPerFile * processedFiles);
        }
      }

      // Process all data with selected branch (for non-transaction data, or small transaction files)
      if (!transactionStats) {
        setStatus("Processing all data in database...");
        setProgress(95);

        const response = await axios.post(`${API_BASE_URL}/api/processAllCsvData`, {
          branchId: parseInt(selectedBranch)
        });

        if (response.data.success) {
          setProgress(100);
          setStatus("✅ Successfully processed all data!");
          setStats(response.data.stats);
        } else {
          throw new Error(response.data.message || "Processing failed");
        }
      } else {
        // For large transaction files, we still need to process member/loan data
        if (memberFile || loanFile || loanDueFile) {
          setStatus("Processing member and loan data...");
          const response = await axios.post(`${API_BASE_URL}/api/processAllCsvData`, {
            branchId: parseInt(selectedBranch)
          });

          if (response.data.success) {
            // Combine stats
            const combinedStats = {
              ...response.data.stats,
              transactionsProcessed: transactionStats.processed,
              transactionsSkipped: transactionStats.skipped,
              transactionErrors: transactionStats.errors
            };
            setStats(combinedStats);
          }
        } else {
          // Only transactions were uploaded
          setStats({
            transactionsProcessed: transactionStats.processed,
            transactionsSkipped: transactionStats.skipped,
            transactionErrors: transactionStats.errors
          });
        }

        setProgress(100);
        setStatus("✅ Successfully processed all data!");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "An error occurred during upload");
      setStatus("❌ Upload failed");
    } finally {
      setLoading(false);
    }
  }, [memberFile, loanFile, loanDueFile, transactionFile, selectedBranch]);

  // Reset form
  const handleReset = () => {
    setMemberFile(null);
    setLoanFile(null);
    setLoanDueFile(null);
    setTransactionFile(null);
    setProgress(0);
    setStatus("");
    setStats(null);
    setError(null);
    setFileStats({ members: 0, loans: 0, loanDues: 0, transactions: 0 });

    // Reset file inputs
    const inputs = document.querySelectorAll('input[type="file"]');
    inputs.forEach((input) => (input.value = ""));
  };

  return (
    <section
      style={{
        minHeight: "100vh",
        width: "100%",
        backgroundColor: "#0f0f0f",
        padding: "40px 20px",
      }}
    >
      <div
        style={{
          maxWidth: "900px",
          margin: "0 auto",
        }}
      >
        {/* Header */}
        <Card
          style={{
            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
            border: "1px solid #0f3460",
            borderRadius: "20px",
            marginBottom: "30px",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
          }}
        >
          <Card.Body style={{ padding: "30px" }}>
            <h2
              style={{
                color: "#ffffff",
                marginBottom: "10px",
                fontSize: "28px",
                fontWeight: "600",
              }}
            >
              📊 Bulk Data Import
            </h2>
            <p style={{ color: "#a8a8a8", marginBottom: 0 }}>
              Upload Member, Loan, and Transaction CSV files to import all data
              into the database.
            </p>
          </Card.Body>
        </Card>

        {/* File Upload Section */}
        <Card
          style={{
            background: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: "15px",
            marginBottom: "20px",
          }}
        >
          <Card.Body style={{ padding: "25px" }}>
            {/* Branch Selector */}
            <div style={{ marginBottom: "25px" }}>
              <h5 style={{ color: "#fff", marginBottom: "15px" }}>
                🏢 Select Branch
              </h5>
              <Form.Select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                disabled={loading}
                style={{
                  backgroundColor: "#333",
                  color: "#fff",
                  border: "2px solid #555",
                  borderRadius: "10px",
                  padding: "12px 15px",
                  fontSize: "16px",
                  maxWidth: "300px",
                }}
              >
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </Form.Select>
              <small style={{ color: "#888", display: "block", marginTop: "8px" }}>
                ⚠️ Select the correct branch before uploading
              </small>
            </div>

            <hr style={{ borderColor: "#444", margin: "20px 0" }} />

            <h5 style={{ color: "#fff", marginBottom: "20px" }}>
              📁 Select Files to Upload
            </h5>

            <Row>
              {/* Member File */}
              <Col md={6}>
                <Card
                  style={{
                    background: memberFile ? "#1e3a5f" : "#252525",
                    border: memberFile ? "2px solid #4dabf7" : "1px solid #444",
                    borderRadius: "12px",
                    marginBottom: "15px",
                  }}
                >
                  <Card.Body>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <span style={{ fontSize: "24px", marginRight: "10px" }}>
                        👤
                      </span>
                      <div>
                        <div style={{ color: "#fff", fontWeight: "500" }}>
                          Member File
                        </div>
                        <small style={{ color: "#888" }}>member_uch.csv</small>
                      </div>
                    </div>
                    <Form.Control
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleMemberFileChange}
                      disabled={loading}
                      size="sm"
                      style={{
                        backgroundColor: "#333",
                        color: "#fff",
                        border: "1px solid #555",
                      }}
                    />
                    {fileStats.members > 0 && (
                      <Badge
                        bg="info"
                        style={{ marginTop: "10px", fontSize: "12px" }}
                      >
                        {fileStats.members} records
                      </Badge>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Loan File */}
              <Col md={6}>
                <Card
                  style={{
                    background: loanFile ? "#3d1e5f" : "#252525",
                    border: loanFile ? "2px solid #9775fa" : "1px solid #444",
                    borderRadius: "12px",
                    marginBottom: "15px",
                  }}
                >
                  <Card.Body>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <span style={{ fontSize: "24px", marginRight: "10px" }}>
                        💰
                      </span>
                      <div>
                        <div style={{ color: "#fff", fontWeight: "500" }}>
                          Loan File
                        </div>
                        <small style={{ color: "#888" }}>loandtl_uch.csv</small>
                      </div>
                    </div>
                    <Form.Control
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleLoanFileChange}
                      disabled={loading}
                      size="sm"
                      style={{
                        backgroundColor: "#333",
                        color: "#fff",
                        border: "1px solid #555",
                      }}
                    />
                    {fileStats.loans > 0 && (
                      <Badge
                        bg="secondary"
                        style={{ marginTop: "10px", fontSize: "12px" }}
                      >
                        {fileStats.loans} records
                      </Badge>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Loan Due Details File (UCH specific) */}
              <Col md={6}>
                <Card
                  style={{
                    background: loanDueFile ? "#5f4d1e" : "#252525",
                    border: loanDueFile ? "2px solid #ffd43b" : "1px solid #444",
                    borderRadius: "12px",
                    marginBottom: "15px",
                  }}
                >
                  <Card.Body>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <span style={{ fontSize: "24px", marginRight: "10px" }}>
                        📊
                      </span>
                      <div>
                        <div style={{ color: "#fff", fontWeight: "500" }}>
                          Loan Due Details
                        </div>
                        <small style={{ color: "#888" }}>loan_duedtl_uch.csv</small>
                      </div>
                    </div>
                    <Form.Control
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleLoanDueFileChange}
                      disabled={loading}
                      size="sm"
                      style={{
                        backgroundColor: "#333",
                        color: "#fff",
                        border: "1px solid #555",
                      }}
                    />
                    {fileStats.loanDues > 0 && (
                      <Badge
                        bg="warning"
                        style={{ marginTop: "10px", fontSize: "12px" }}
                      >
                        {fileStats.loanDues} records
                      </Badge>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Transaction File */}
              <Col md={6}>
                <Card
                  style={{
                    background: transactionFile ? "#1e5f3d" : "#252525",
                    border: transactionFile
                      ? "2px solid #51cf66"
                      : "1px solid #444",
                    borderRadius: "12px",
                    marginBottom: "15px",
                  }}
                >
                  <Card.Body>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <span style={{ fontSize: "24px", marginRight: "10px" }}>
                        📋
                      </span>
                      <div>
                        <div style={{ color: "#fff", fontWeight: "500" }}>
                          Transaction File
                        </div>
                        <small style={{ color: "#888" }}>txndtl_uch.csv</small>
                      </div>
                    </div>
                    <Form.Control
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleTransactionFileChange}
                      disabled={loading}
                      size="sm"
                      style={{
                        backgroundColor: "#333",
                        color: "#fff",
                        border: "1px solid #555",
                      }}
                    />
                    {fileStats.transactions > 0 && (
                      <Badge
                        bg="success"
                        style={{ marginTop: "10px", fontSize: "12px" }}
                      >
                        {fileStats.transactions} records
                      </Badge>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                gap: "15px",
                marginTop: "20px",
              }}
            >
              <Button
                variant="primary"
                size="lg"
                onClick={handleFileUpload}
                disabled={loading || (!memberFile && !loanFile && !transactionFile)}
                style={{
                  background:
                    "linear-gradient(135deg, #4dabf7 0%, #3a7bd5 100%)",
                  border: "none",
                  borderRadius: "10px",
                  padding: "12px 30px",
                  fontWeight: "600",
                  boxShadow: "0 4px 15px rgba(77, 171, 247, 0.4)",
                }}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                      style={{ marginRight: "10px" }}
                    ></span>
                    Processing...
                  </>
                ) : (
                  "🚀 Upload & Process Data"
                )}
              </Button>

              <Button
                variant="outline-secondary"
                size="lg"
                onClick={handleReset}
                disabled={loading}
                style={{
                  borderRadius: "10px",
                  padding: "12px 20px",
                }}
              >
                🔄 Reset
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Progress Section */}
        {(loading || progress > 0) && (
          <Card
            style={{
              background: "#1a1a1a",
              border: "1px solid #333",
              borderRadius: "15px",
              marginBottom: "20px",
            }}
          >
            <Card.Body style={{ padding: "25px" }}>
              <h5 style={{ color: "#fff", marginBottom: "15px" }}>
                ⏳ Progress
              </h5>
              <ProgressBar
                now={progress}
                label={`${progress}%`}
                animated={loading}
                variant={progress === 100 ? "success" : "info"}
                style={{
                  height: "25px",
                  borderRadius: "10px",
                  marginBottom: "15px",
                }}
              />
              <p
                style={{
                  color: "#a8a8a8",
                  marginBottom: 0,
                  textAlign: "center",
                  fontSize: "14px",
                }}
              >
                {status}
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Error Alert */}
        {error && (
          <Alert
            variant="danger"
            style={{
              borderRadius: "15px",
              marginBottom: "20px",
              background: "#2c1a1a",
              border: "1px solid #5c2626",
              color: "#ff6b6b",
            }}
          >
            <Alert.Heading>❌ Error</Alert.Heading>
            <p>{error}</p>
          </Alert>
        )}

        {/* Success Statistics */}
        {stats && (
          <Card
            style={{
              background:
                "linear-gradient(135deg, #1a2e1a 0%, #1e3a1e 100%)",
              border: "1px solid #2d5a2d",
              borderRadius: "15px",
            }}
          >
            <Card.Body style={{ padding: "25px" }}>
              <h5
                style={{
                  color: "#51cf66",
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                ✅ Import Completed Successfully!
              </h5>

              <Table
                borderless
                style={{ color: "#fff", marginBottom: 0 }}
              >
                <tbody>
                  <tr>
                    <td style={{ padding: "10px 15px" }}>
                      <span style={{ marginRight: "10px" }}>👤</span>
                      Members Processed
                    </td>
                    <td style={{ textAlign: "right", padding: "10px 15px" }}>
                      <Badge bg="info" style={{ fontSize: "14px" }}>
                        {stats.membersProcessed || 0}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "10px 15px" }}>
                      <span style={{ marginRight: "10px" }}>💰</span>
                      Loans Processed
                    </td>
                    <td style={{ textAlign: "right", padding: "10px 15px" }}>
                      <Badge bg="primary" style={{ fontSize: "14px" }}>
                        {stats.loansProcessed || 0}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "10px 15px" }}>
                      <span style={{ marginRight: "10px" }}>📋</span>
                      Transactions Processed
                    </td>
                    <td style={{ textAlign: "right", padding: "10px 15px" }}>
                      <Badge bg="success" style={{ fontSize: "14px" }}>
                        {stats.transactionsProcessed || 0}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "10px 15px" }}>
                      <span style={{ marginRight: "10px" }}>🏦</span>
                      Accounts Created
                    </td>
                    <td style={{ textAlign: "right", padding: "10px 15px" }}>
                      <Badge bg="secondary" style={{ fontSize: "14px" }}>
                        {stats.accountsCreated || 0}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "10px 15px" }}>
                      <span style={{ marginRight: "10px" }}>🔄</span>
                      Duplicates Skipped
                    </td>
                    <td style={{ textAlign: "right", padding: "10px 15px" }}>
                      <Badge bg="warning" style={{ fontSize: "14px" }}>
                        {stats.duplicatesSkipped || 0}
                      </Badge>
                    </td>
                  </tr>
                  {stats.errors > 0 && (
                    <tr>
                      <td style={{ padding: "10px 15px" }}>
                        <span style={{ marginRight: "10px" }}>⚠️</span>
                        Errors
                      </td>
                      <td style={{ textAlign: "right", padding: "10px 15px" }}>
                        <Badge bg="danger" style={{ fontSize: "14px" }}>
                          {stats.errors || 0}
                        </Badge>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {/* Help Section */}
        <Card
          style={{
            background: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: "15px",
            marginTop: "20px",
          }}
        >
          <Card.Body style={{ padding: "25px" }}>
            <h5 style={{ color: "#fff", marginBottom: "15px" }}>
              ℹ️ How to Use
            </h5>
            <ol style={{ color: "#a8a8a8", marginBottom: 0, paddingLeft: "20px" }}>
              <li style={{ marginBottom: "10px" }}>
                <strong style={{ color: "#4dabf7" }}>Member File (member_uch.csv)</strong>:
                Contains customer personal details like Name, Address, Phone, DOB, AadhaarNo
              </li>
              <li style={{ marginBottom: "10px" }}>
                <strong style={{ color: "#9775fa" }}>Loan File (loandtl_uch.csv)</strong>:
                Contains loan information linked to customers via AadhaarNo, AccNo
              </li>
              <li style={{ marginBottom: "10px" }}>
                <strong style={{ color: "#51cf66" }}>Transaction File (txndtl_uch.csv)</strong>:
                Contains transaction history linked via LedgerID
              </li>
              <li style={{ marginBottom: "10px" }}>
                The system automatically <strong>deduplicates</strong> customers based on
                AadhaarNo, AccNo, and Mobile Number
              </li>
              <li>
                Customers with loans are automatically linked to their loan records
              </li>
            </ol>
          </Card.Body>
        </Card>
      </div>
    </section>
  );
};

export default UploadMembers;